<template>
  <WidgetChart
    v-if="!isLoading"
    title="Ventas por elementoTipo"
  >
    <template #default>
      <div class="flex flex-col md:flex-row gap-4 mb-4">
        <div style="margin-top: 3%">
          <custom-select
            v-model="selectedTipos"
            :label="'Tipo de producto'"
            :spanColumn="16"
            :settings="{
              placeholder: 'Seleccionar productos',
              options: productTypeList,
              multiple: true,
            }"
          >
          </custom-select>
        </div>
        <div
          class="flex-none self-center"
          style="margin-top: 5%"
        >
          <div class="text-sm text-gray-600">
            Total ventas: <strong>{{ totalFormatted }}</strong>
          </div>
        </div>
      </div>
      <div
        v-if="error"
        class="text-red-600 mb-2"
      >
        {{ error }}
      </div>

      <div v-else>
        <VueDataUi
          v-if="hasData"
          component="VueUiVerticalBar"
          :dataset="dataset"
          :config="config"
        />
        <div
          v-else
          class="text-sm text-gray-500"
        >
          No hay datos para el periodo y filtros seleccionados.
        </div>
      </div>
    </template>
  </WidgetChart>
</template>

<script setup>
import {
  ref,
  watch,
  onMounted,
  computed,
} from 'vue';
import {
  VueDataUi,
  getVueDataUiConfig,
} from 'vue-data-ui';
import useApi from '@/shared/composables/useApi';
import WidgetChart from './base/chart.vue';
import { buildDateRangeQueryParams } from './utils';
import { useElementoTipoStore } from '@/stores/elemento_tipo/elemento_tipo_store.js';
import CustomSelect from '@/shared/components/custom_select.vue';
import { storeToRefs } from 'pinia';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});
const elementoTipoStore = useElementoTipoStore();
const { productTypeList } = storeToRefs(elementoTipoStore);
const { firstTipoProducto } = elementoTipoStore;
const { isLoading, apiGet } = useApi();
const error = ref('');
const selectedTipos = ref([]);
const rawData = ref([]);
const dataset = ref([]);
const config = ref(getVueDataUiConfig('vue_ui_vertical_bar'));

// Configuración visual básica
config.value.legend = { show: false };
config.value.title = { show: true, text: 'Ventas por elementoTipo' };
config.value.axis = {
  x: { show: true },
  y: {
    show: true,
    label: {
      formatter: ({ value }) => {
        const n = Number(value);
        if (isNaN(n)) return '';
        return n.toLocaleString(navigator.language, {
          style: 'currency',
          currency: 'ARS',
          minimumFractionDigits: 0,
        });
      },
    },
  },
};
config.value.tooltip = {
  show: true,
  formatter: ({ datapoint }) => {
    if (!datapoint) return '';
    return `${datapoint.name}: ${datapoint.value}`;
  },
};

const hasData = computed(() => Array.isArray(dataset.value) && dataset.value.length > 0);

const total = computed(() =>
  rawData.value.reduce((sum, item) => sum + (typeof item.value === 'number' ? item.value : 0), 0),
);
const totalFormatted = computed(() => Number(total.value).toLocaleString(navigator.language));

const buildQueryParams = () => {
  const base = buildDateRangeQueryParams(props.filters);
  if (selectedTipos.value && selectedTipos.value.length) {
    const ids = selectedTipos.value.map((t) => t.Codigo);
    return { ...base, CodigoElementoTipo: ids };
  }
  return base;
};

const normalize = (arr) =>
  arr
    .map((item) => ({
      name: (item.label || '').trim(),
      value: typeof item.value === 'number' ? item.value : parseFloat(item.value) || 0,
    }))
    .filter((it) => it.name)
    .sort((a, b) => b.value - a.value);

const fetchData = async () => {
  error.value = '';
  try {
    const params = buildQueryParams();
    const res = await apiGet('/dashboard/ventas-por-elemento-tipo', { params });

    const normalized = normalize(Array.isArray(res.data) ? res.data : []);
    rawData.value = normalized;
    dataset.value = normalized.map((i) => ({
      name: i.name,
      value: i.value,
    }));
  } catch (e) {
    console.error('Error fetching ventas por elementoTipo', e);
    error.value = 'No se pudieron cargar los datos para el gráfico.';
    rawData.value = [];
    dataset.value = [];
  }
};

onMounted(() => {
  firstTipoProducto();
});

watch(
  () => [props.filters, selectedTipos.value],
  () => {
    if (selectedTipos.value.length) fetchData();
    else dataset.value = [];
  },
  { deep: true },
);
</script>
